import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as moment from 'moment';
import { SharedDataService } from 'src/app/services/app/shared-service/shared-data.service';
import { CredentialService } from 'src/app/services/core/credential/credential.service';
import { WebPushService } from 'src/app/services/app/web-push/web-push.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';

@UntilDestroy()
@Component({
  selector: 'app-notification-modal',
  templateUrl: './notification-modal.component.html',
  styleUrls: ['./notification-modal.component.css']
})
export class NotificationModalComponent implements OnInit {
  notifications: any = [];
  category = '';
  categoryTabs = [
    { value: '', label: 'All' },
    { value: 'ticket', label: 'Tickets' },
    { value: 'other', label: 'Others' },
  ];
  @Output() closeModal = new EventEmitter<any>();
  user: any;
  currentDate: any = [];
  timeZone: any;
  preCompanies: any;
  unreadCount = 0;
  isPushLoading = false;
  isPushSubscribed = false;
  permissionStatus = 'default';
  isPushSupported = true;

  constructor(
    private sharedDataService: SharedDataService,
    private credentialService: CredentialService,
    private router: Router,
    public webPushService: WebPushService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) { }
  ngOnInit(): void {
    this.getUser();
    this.getAllCompanies();
    this.sharedDataService.refreshUnreadNotificationCount();
    this.sharedDataService
      .getUnreadNotificationCount()
      .pipe(untilDestroyed(this))
      .subscribe(count => {
        this.unreadCount = count;
        this.cdr.detectChanges();
      });

    this.webPushService.syncSubscriptionStatus().then(() => {
      this.cdr.detectChanges();
    });

    this.webPushService.isSubscribed$
      .pipe(untilDestroyed(this))
      .subscribe(sub => {
        this.isPushSubscribed = sub;
        this.cdr.detectChanges();
      });

    this.webPushService.permissionStatus$
      .pipe(untilDestroyed(this))
      .subscribe(status => {
        this.permissionStatus = status;
        this.cdr.detectChanges();
      });

    this.webPushService.isLoading$
      .pipe(untilDestroyed(this))
      .subscribe(loading => {
        this.isPushLoading = loading;
        this.cdr.detectChanges();
      });

    this.webPushService.isSupported$
      .pipe(untilDestroyed(this))
      .subscribe(supported => {
        this.isPushSupported = supported;
        this.cdr.detectChanges();
      });
  }

  async toggleWebPush() {
    this.isPushLoading = true;
    this.cdr.detectChanges();

    try {
      if (this.isPushSubscribed) {
        const res = await this.webPushService.unsubscribe();
        this.toastService.show(res.message);
      } else {
        const res = await this.webPushService.requestPermissionAndSubscribe();
        this.toastService.show(res.message);
      }
    } finally {
      this.isPushLoading = false;
      this.cdr.detectChanges();
    }
  }

  sendTestPush() {
    this.isPushLoading = true;
    this.webPushService.sendTestNotification().subscribe({
      next: () => {
        this.isPushLoading = false;
        this.toastService.show('Test notification sent! Check your desktop notifications.');
      },
      error: (err) => {
        this.isPushLoading = false;
        this.toastService.show(err?.error?.message || 'Failed to dispatch test notification.');
      }
    });
  }

  onMarkAllAsRead() {
    this.sharedDataService.markAllAsRead(this.user?.company).subscribe({
      next: () => {
        if (Array.isArray(this.notifications)) {
          this.notifications.forEach((n: any) => n.isRead = true);
        }
        this.unreadCount = 0;
        this.sharedDataService.refreshUnreadNotificationCount(true);
        this.toastService.show('All notifications marked as read.');
        this.cdr.detectChanges();
      },
      error: () => {
        this.toastService.show('Failed to mark all as read.');
      }
    });
  }

  hasUnreadNotifications(): boolean {
    return Array.isArray(this.notifications) && this.notifications.some((n: any) => !n.isRead);
  }

  getUser() {
    this.sharedDataService.getUser().pipe(untilDestroyed(this)).subscribe((data) => {
      if (data) {
        this.user = data;
        this.timeZone = data.timeZone
        this.currentDate = [moment().startOf('day').tz(this.timeZone).toISOString(), moment().endOf('day').tz(this.timeZone).toISOString()];
        this.getNotificationsList();
      }

    })
  }

  getAllCompanies() {
     const queryParams = {
       limit: 9999,
      //  isActive: true
     };
    this.credentialService.getPicker(queryParams).subscribe((data: any) => {
      if (data && data.results) {
        this.preCompanies = data.results.reduce((obj: any, item: any) => ((obj[item.id] = item), obj), {});
      }
    });
  }

  onSelectCategory(category: string) {
    this.category = category;
    this.getNotificationsList();
  }

  onCloseModal() {
    this.closeModal.emit();
  }


  getNotificationsList() {
    const queryParams: any = {
      sortBy: 'createdAt:desc',
      time: this.currentDate
    };
    if (this.category) {
      queryParams.category = this.category;
    }
    this.sharedDataService
      .getNotifications(queryParams)
      // .pipe(takeUntil(componentDestroyed(this)))
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data) {
          this.notifications = data.results;
        }
      });
  }

  onNotificationClick(notification: any) {
    this.markAsRead(notification);
    // a support notification opens its ticket or chat
    if (notification.type === 'support' && notification.metadata?.url) {
      this.router.navigateByUrl(notification.metadata.url);
      this.closeModal.emit();
      return;
    }
    if (notification.type === 'trip') {
      this.goToTrips(notification);
    }
    else {
      this.goToOnboarding(notification);
    }
    this.closeModal.emit();
  }

  onMarkSingleAsRead(event: MouseEvent, notification: any) {
    event.stopPropagation();
    this.markAsRead(notification);
  }

  markAsRead(notification: any) {
    if (!notification || notification.isRead) {
      return;
    }
    notification.isRead = true;
    if (this.unreadCount > 0) {
      this.unreadCount -= 1;
    }
    this.sharedDataService.decrementUnreadNotificationCount(1);
    this.cdr?.detectChanges();

    // Not piped through untilDestroyed: this modal closes (and gets
    // destroyed) right after a click, which would otherwise cancel the
    // in-flight request before the server ever receives it.
    this.sharedDataService
      .markNotificationAsRead(notification.id || notification._id)
      .subscribe({
        next: () => {},
        error: () => {}
      });
  }

  // goToTrips(notification: any) {
  //   this.router.navigate(['trips'], { fragment: notification?.trip?.status });
  // }
  
  goToTrips(notification: any) {
  if (notification?.trip) {
    // Save the full trip in the service
    this.sharedDataService.setTripRedirect(notification.trip);

    // Navigate with fragment only for filtering
    this.router.navigate(['trips'], { fragment: notification.trip.status });
  }
}


  goToOnboarding(notification: any) {
    const { type, driver, vehicle, company, signup, member } = notification;
    if (signup) {
      this.router.navigate(['signups']);
      return;
    }
    if (member) {
      this.router.navigate(['members']);
      return;
    }
    let companyId;
    let entityType;
    let entity;
    if (driver) {
      companyId = driver.company;
      entityType = 'drivers';
      entity = driver;
    } else if (vehicle) {
      companyId = vehicle.company;
      entityType = 'vehicles';
      entity = vehicle;
    } else if (company) {
      companyId = company.id;
      entityType = 'company';
      entity = company;
    }
    if (companyId) {
      const redirect = {
        entityType,
        entity
      };
      this.sharedDataService.setOnboardingRedirectEntity(redirect);
      this.router.navigate(['credentials', companyId], { 
        fragment: entityType 
      });
    }
  }

  getEntityType(notification: any) {
    const { driver, vehicle, company, trip, signup, member } = notification;
    let entityType;
    if (driver) {
      entityType = 'drivers';
    } else if (vehicle) {
      entityType = 'vehicles';
    } else if (trip) {
      entityType = 'trip';
    } else if (company) {
      entityType = 'company';
    } else if (signup) {
      entityType = 'signup';
    } else if (member) {
      entityType = 'member';
    }
    return entityType;
  }

  getRelativeTime(date: any): string {
    return date ? moment(date).fromNow() : '';
  }
}
